import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Action types
const VOCABULARY_ACTIONS = {
  // Data loading
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_VOCABULARY_DATA: 'SET_VOCABULARY_DATA',
  
  // App navigation
  SET_APP_STATE: 'SET_APP_STATE',
  SET_CURRENT_VIEW: 'SET_CURRENT_VIEW', // 'categories' | 'pools' | 'session' | 'results'
  SET_SELECTED_CATEGORY: 'SET_SELECTED_CATEGORY',
  
  // New pool selection system
  TOGGLE_POOL: 'TOGGLE_POOL',
  CLEAR_SELECTED_POOLS: 'CLEAR_SELECTED_POOLS',
  SET_SELECTED_POOLS: 'SET_SELECTED_POOLS',
  
  // Session configuration
  SET_SESSION_MODE: 'SET_SESSION_MODE',
  SET_SELECTED_CATEGORY_LEVELS: 'SET_SELECTED_CATEGORY_LEVELS',
  TOGGLE_CATEGORY: 'TOGGLE_CATEGORY',
  TOGGLE_CATEGORY_LEVEL: 'TOGGLE_CATEGORY_LEVEL',
  START_SESSION: 'START_SESSION',
  START_POOL_SESSION: 'START_POOL_SESSION', // New action for pool-based sessions
  
  // Session progress
  SET_CURRENT_WORD_INDEX: 'SET_CURRENT_WORD_INDEX',
  SET_USER_ANSWER: 'SET_USER_ANSWER',
  SET_SHOW_RESULT: 'SET_SHOW_RESULT',
  SET_IS_CORRECT: 'SET_IS_CORRECT',
  ADD_CORRECT_ANSWER: 'ADD_CORRECT_ANSWER',
  ADD_INCORRECT_ANSWER: 'ADD_INCORRECT_ANSWER',
  NEXT_WORD: 'NEXT_WORD',
  
  // Settings
  SET_TRANSLATION_DIRECTION: 'SET_TRANSLATION_DIRECTION',
  
  // Sentence exercises
  SET_EXERCISE_TYPE: 'SET_EXERCISE_TYPE', // 'vocabulary' | 'sentences'
  SET_SENTENCES_DATA: 'SET_SENTENCES_DATA',
  SET_SELECTED_TENSE: 'SET_SELECTED_TENSE', // 'present' | 'past' | 'future'
  SET_SELECTED_STAGE: 'SET_SELECTED_STAGE', // '01' | '02'
  START_SENTENCE_SESSION: 'START_SENTENCE_SESSION',
  START_WORD_SESSION: 'START_WORD_SESSION', // New action for word flashcards
  START_RANDOM_WORD_SESSION: 'START_RANDOM_WORD_SESSION', // Random session from category
  START_RANDOM_SENTENCE_SESSION: 'START_RANDOM_SENTENCE_SESSION', // Random session from stage
  
  // Legacy (for gradual migration)
  SET_CURRENT_CATEGORY: 'SET_CURRENT_CATEGORY',
  UPDATE_SCORE: 'UPDATE_SCORE',
  ADD_COMPLETED_WORD: 'ADD_COMPLETED_WORD',
  RESET_COMPLETED_WORDS: 'RESET_COMPLETED_WORDS',
  SET_SHUFFLED_WORDS: 'SET_SHUFFLED_WORDS',
  SET_IS_RANDOM_MODE: 'SET_IS_RANDOM_MODE',
  SET_SHOW_CATEGORY_POPUP: 'SET_SHOW_CATEGORY_POPUP',
  SET_SHOW_SETTINGS_POPUP: 'SET_SHOW_SETTINGS_POPUP',
  RESET_QUIZ: 'RESET_QUIZ'
};

// Initial state
const initialState = {
  // Data
  vocabularyData: {},
  sentencesData: {},
  loading: true,
  error: null,
  
  // App navigation
  appState: 'menu', // 'menu' | 'session' | 'results'
  currentView: 'exercise-type-selector', // 'exercise-type-selector' | 'categories' | 'pools' | 'sentence-categories' | 'sentence-subcategories' | 'session' | 'results'
  exerciseType: null, // 'vocabulary' | 'sentences'
  selectedCategory: null, // for navigating to pools view
  selectedTense: null, // 'present' | 'past' | 'future'
  selectedStage: null, // '01' | '02'
  
  // New pool selection system
  selectedPools: new Set(), // Set of pool codes like 'A1-01-01'
  
  // Session configuration
  sessionConfig: {
    mode: 'quiz', // 'quiz' | 'study'
    type: 'vocabulary', // 'vocabulary' | 'sentences'
    selectedCategoryLevels: {}, // { 'basic_words': [1, 2], 'food': [1] }
    words: [], // filtered and shuffled words for session
    sentences: [], // sentences for sentence exercises
    translationDirection: 'pl-de' // 'pl-de' lub 'de-pl'
  },
  
  // Session progress
  sessionProgress: {
    currentWordIndex: 0,
    userAnswer: '',
    showResult: false,
    isCorrect: false,
    correctAnswers: [], // {word, category, userAnswer, isCorrect: true}
    incorrectAnswers: [], // {word, category, userAnswer, correctAnswer, isCorrect: false}
    startTime: null,
    endTime: null
  },
  
  // Legacy state (for backward compatibility during migration)
  currentCategory: 'basic_words',
  score: { correct: 0, total: 0 },
  completedWords: new Set(),
  shuffledWords: [],
  isRandomMode: false,
  showCategoryPopup: false,
  showSettingsPopup: false
};

// Utility function to filter words by categories and levels (legacy)
function filterWordsByConfig(vocabularyData, selectedCategoryLevels) {
  const allWords = [];
  
  Object.entries(selectedCategoryLevels).forEach(([categoryKey, levels]) => {
    const category = vocabularyData[categoryKey];
    if (category && category.words && levels.length > 0) {
      category.words.forEach(word => {
        // Filter by difficulty level
        if (levels.includes(word.difficulty)) {
          allWords.push({
            ...word,
            categoryKey,
            categoryName: category.name
          });
        }
      });
    }
  });
  
  // Shuffle the words
  return shuffleArray(allWords);
}

// New utility function to filter words by selected pools
function filterWordsByPools(vocabularyData, selectedPools) {
  const allWords = [];
  const pools = vocabularyData?.pools || {};
  
  selectedPools.forEach(poolCode => {
    const pool = pools[poolCode];
    if (pool && pool.words && pool.words.length > 0) {
      pool.words.forEach(word => {
        allWords.push({
          ...word,
          poolCode,
          poolName: pool.name,
          poolDifficulty: pool.difficulty,
          categoryKey: pool.category
        });
      });
    }
  });
  
  // Shuffle the words
  return shuffleArray(allWords);
}

// Utility function to shuffle array
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Reducer function
function vocabularyReducer(state, action) {
  switch (action.type) {
    case VOCABULARY_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case VOCABULARY_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
      
    case VOCABULARY_ACTIONS.SET_VOCABULARY_DATA:
      return { ...state, vocabularyData: action.payload, loading: false, error: null };
      
    // App navigation
    case VOCABULARY_ACTIONS.SET_APP_STATE:
      return { ...state, appState: action.payload };
      
    case VOCABULARY_ACTIONS.SET_CURRENT_VIEW:
      return { ...state, currentView: action.payload };
      
    case VOCABULARY_ACTIONS.SET_SELECTED_CATEGORY:
      return { ...state, selectedCategory: action.payload };
      
    // New pool selection system
    case VOCABULARY_ACTIONS.TOGGLE_POOL:
      const newSelectedPools = new Set(state.selectedPools);
      const togglePoolCode = action.payload;
      
      if (newSelectedPools.has(togglePoolCode)) {
        newSelectedPools.delete(togglePoolCode);
      } else {
        newSelectedPools.add(togglePoolCode);
      }
      
      return { ...state, selectedPools: newSelectedPools };
      
    case VOCABULARY_ACTIONS.CLEAR_SELECTED_POOLS:
      return { ...state, selectedPools: new Set() };
      
    case VOCABULARY_ACTIONS.SET_SELECTED_POOLS:
      return { ...state, selectedPools: new Set(action.payload) };
      
    // Session configuration
    case VOCABULARY_ACTIONS.SET_SESSION_MODE:
      return { 
        ...state, 
        sessionConfig: { ...state.sessionConfig, mode: action.payload }
      };
      
    case VOCABULARY_ACTIONS.SET_SELECTED_CATEGORY_LEVELS:
      return { 
        ...state, 
        sessionConfig: { ...state.sessionConfig, selectedCategoryLevels: action.payload }
      };
      
        case VOCABULARY_ACTIONS.TOGGLE_CATEGORY: {
      const currentCategoryLevels = { ...state.sessionConfig.selectedCategoryLevels };
      const categoryKey = action.payload;
      
      if (currentCategoryLevels[categoryKey]) {
        // Category is selected, remove it
        delete currentCategoryLevels[categoryKey];
      } else {
        // Category is not selected, add it with default level 1
        currentCategoryLevels[categoryKey] = [1];
      }
      
      return { 
        ...state, 
        sessionConfig: { ...state.sessionConfig, selectedCategoryLevels: currentCategoryLevels }
      };
    }
      
    case VOCABULARY_ACTIONS.TOGGLE_CATEGORY_LEVEL: {
      const { categoryKey: catKey, level } = action.payload;
      const currentLevels = state.sessionConfig.selectedCategoryLevels[catKey] || [];
      const newLevels = currentLevels.includes(level)
        ? currentLevels.filter(l => l !== level)
        : [...currentLevels, level].sort((a, b) => a - b);
      
      return {
        ...state,
        sessionConfig: { 
          ...state.sessionConfig, 
          selectedCategoryLevels: { 
            ...state.sessionConfig.selectedCategoryLevels, 
            [catKey]: newLevels 
          } 
        }
      };
    }
      
    case VOCABULARY_ACTIONS.START_SESSION: {
      const filteredWords = filterWordsByConfig(
        state.vocabularyData, 
        state.sessionConfig.selectedCategoryLevels
      );
      return {
        ...state,
        appState: 'session',
        sessionConfig: { ...state.sessionConfig, words: filteredWords },
        sessionProgress: {
          currentWordIndex: 0,
          userAnswer: '',
          showResult: false,
          isCorrect: false,
          correctAnswers: [],
          incorrectAnswers: [],
          startTime: new Date(),
          endTime: null
        }
      };
    }
      
    case VOCABULARY_ACTIONS.START_POOL_SESSION: {
      const { mode, selectedPools } = action.payload;
      console.log('START_POOL_SESSION:', { mode, selectedPools }); // Debug log
      const poolWords = filterWordsByPools(state.vocabularyData, selectedPools);
      return {
        ...state,
        appState: 'session',
        sessionConfig: { 
          ...state.sessionConfig, 
          type: 'vocabulary', // Explicitly set type for vocabulary sessions
          mode: mode,
          words: poolWords,
          sentences: [], // Clear sentences for vocabulary sessions
          poolCode: selectedPools.length > 0 ? selectedPools[0] : null // Set poolCode for SRS
        },
        sessionProgress: {
          currentWordIndex: 0,
          userAnswer: '',
          showResult: false,
          isCorrect: false,
          correctAnswers: [],
          incorrectAnswers: [],
          startTime: new Date(),
          endTime: null
        }
      };
    }
      
    // Session progress
    case VOCABULARY_ACTIONS.SET_USER_ANSWER:
      return { 
        ...state, 
        sessionProgress: { ...state.sessionProgress, userAnswer: action.payload }
      };
      
    case VOCABULARY_ACTIONS.SET_SHOW_RESULT:
      return { 
        ...state, 
        sessionProgress: { ...state.sessionProgress, showResult: action.payload }
      };
      
    case VOCABULARY_ACTIONS.SET_IS_CORRECT:
      return { 
        ...state, 
        sessionProgress: { ...state.sessionProgress, isCorrect: action.payload }
      };
      
    case VOCABULARY_ACTIONS.ADD_CORRECT_ANSWER:
      return {
        ...state,
        sessionProgress: {
          ...state.sessionProgress,
          correctAnswers: [...state.sessionProgress.correctAnswers, action.payload]
        }
      };
      
    case VOCABULARY_ACTIONS.ADD_INCORRECT_ANSWER:
      return {
        ...state,
        sessionProgress: {
          ...state.sessionProgress,
          incorrectAnswers: [...state.sessionProgress.incorrectAnswers, action.payload]
        }
      };
      
    case VOCABULARY_ACTIONS.NEXT_WORD:
      const nextIndex = state.sessionProgress.currentWordIndex + 1;
      const currentSession = state.sessionConfig;
      const totalItems = currentSession.type === 'sentences' 
        ? currentSession.sentences.length 
        : currentSession.words.length;
      const isSessionComplete = nextIndex >= totalItems;
      
      return {
        ...state,
        appState: isSessionComplete ? 'results' : 'session',
        sessionProgress: {
          ...state.sessionProgress,
          currentWordIndex: nextIndex,
          userAnswer: '',
          showResult: false,
          isCorrect: false,
          endTime: isSessionComplete ? new Date() : null
        }
      };
      
    case VOCABULARY_ACTIONS.SET_TRANSLATION_DIRECTION:
      return { 
        ...state, 
        sessionConfig: { ...state.sessionConfig, translationDirection: action.payload }
      };
      
    // Sentence exercises
    case VOCABULARY_ACTIONS.SET_EXERCISE_TYPE:
      return { ...state, exerciseType: action.payload };
      
    case VOCABULARY_ACTIONS.SET_SENTENCES_DATA:
      return { ...state, sentencesData: action.payload };
      
    case VOCABULARY_ACTIONS.SET_SELECTED_TENSE:
      return { ...state, selectedTense: action.payload };
      
    case VOCABULARY_ACTIONS.SET_SELECTED_STAGE:
      return { ...state, selectedStage: action.payload };
      
    case VOCABULARY_ACTIONS.START_SENTENCE_SESSION: {
      const { stage, poolCode, tense, subcategory, sentences: passedSentences, mode: sessionMode } = action.payload;
      const sentenceData = state.sentencesData;
      let sentences = [];
      
      console.log('START_SENTENCE_SESSION:', { stage, poolCode, tense, subcategory, passedSentences, sessionMode, sentenceData }); // Debug log
      
      // Use sentences passed from component (new pool-based system)
      // These are already SRS-filtered objects with proper id mappings
      if (passedSentences && Array.isArray(passedSentences)) {
        sentences = passedSentences;
        console.log(`Using ${sentences.length} sentences passed from component`);
      }
      // Legacy tense-based system (fallback)
      else if (sentenceData && sentenceData.subcategories && sentenceData.subcategories[subcategory]) {
        sentences = sentenceData.loadedSentences && sentenceData.loadedSentences[tense] 
          ? sentenceData.loadedSentences[tense] 
          : [];
      }
      
      console.log('Final sentences for session:', sentences.length); // Debug log
      
      // Check if sentences are already SRS objects (have 'item' field) - don't re-shuffle in a way that loses id
      // Shuffling is safe because the 'id' field stays with each object
      const shuffledSentences = shuffleArray(sentences);
      
      return {
        ...state,
        appState: 'session',
        sessionConfig: { 
          ...state.sessionConfig, 
          type: 'sentences',
          mode: sessionMode || 'quiz',
          words: [], // Clear words for sentence sessions
          sentences: shuffledSentences,
          translationDirection: 'pl-de', // Only PL->DE for sentences
          poolCode: poolCode // Store poolCode for SRS
        },
        sessionProgress: {
          currentWordIndex: 0,
          userAnswer: '',
          showResult: false,
          isCorrect: false,
          correctAnswers: [],
          incorrectAnswers: [],
          startTime: new Date(),
          endTime: null
        }
      };
    }
      
    case VOCABULARY_ACTIONS.START_WORD_SESSION: {
      const { categoryKey, poolCode: wordPoolCode, words: passedWords, mode: wordSessionMode } = action.payload;
      
      console.log('START_WORD_SESSION:', { categoryKey, wordPoolCode, passedWords, wordSessionMode }); // Debug log
      console.log('Final words for session:', passedWords.length); // Debug log
      
      return {
        ...state,
        appState: 'session',
        currentView: 'session',
        sessionConfig: { 
          ...state.sessionConfig, 
          type: 'vocabulary',
          mode: wordSessionMode || 'flashcards',
          words: shuffleArray(passedWords),
          sentences: [], // Clear sentences for word sessions
          translationDirection: 'pl-de', // Default direction
          poolCode: wordPoolCode // Store poolCode for SRS
        },
        sessionProgress: {
          currentWordIndex: 0,
          userAnswer: '',
          showResult: false,
          isCorrect: false,
          correctAnswers: [],
          incorrectAnswers: [],
          startTime: new Date(),
          endTime: null
        }
      };
    }

    case VOCABULARY_ACTIONS.START_RANDOM_WORD_SESSION: {
      const { categoryKey: randomCategoryKey, words: randomWords, mode: randomMode } = action.payload;
      
      console.log('START_RANDOM_WORD_SESSION:', { randomCategoryKey, wordCount: randomWords.length, randomMode });
      
      return {
        ...state,
        appState: 'session',
        currentView: 'session',
        sessionConfig: { 
          ...state.sessionConfig, 
          type: 'vocabulary',
          mode: randomMode || 'flashcards',
          words: shuffleArray(randomWords),
          sentences: [],
          translationDirection: 'pl-de',
          poolCode: null // No specific pool for random sessions
        },
        sessionProgress: {
          currentWordIndex: 0,
          userAnswer: '',
          showResult: false,
          isCorrect: false,
          correctAnswers: [],
          incorrectAnswers: [],
          startTime: new Date(),
          endTime: null
        }
      };
    }

    case VOCABULARY_ACTIONS.START_RANDOM_SENTENCE_SESSION: {
      const { stage: randomStage, sentences: randomSentences, mode: randomSentenceMode } = action.payload;
      
      console.log('START_RANDOM_SENTENCE_SESSION:', { randomStage, sentenceCount: randomSentences.length, randomSentenceMode });
      
      return {
        ...state,
        appState: 'session',
        currentView: 'session',
        sessionConfig: { 
          ...state.sessionConfig, 
          type: 'sentences',
          mode: randomSentenceMode || 'flashcards',
          sentences: shuffleArray(randomSentences),
          words: [],
          poolCode: null // No specific pool for random sessions
        },
        sessionProgress: {
          currentWordIndex: 0,
          userAnswer: '',
          showResult: false,
          isCorrect: false,
          correctAnswers: [],
          incorrectAnswers: [],
          startTime: new Date(),
          endTime: null
        }
      };
    }
      
    case VOCABULARY_ACTIONS.SET_CURRENT_WORD_INDEX:
      return { 
        ...state, 
        sessionProgress: { ...state.sessionProgress, currentWordIndex: action.payload }
      };
      
    case VOCABULARY_ACTIONS.UPDATE_SCORE:
      return { 
        ...state, 
        score: {
          correct: state.score.correct + (action.payload ? 1 : 0),
          total: state.score.total + 1
        }
      };
      
    case VOCABULARY_ACTIONS.ADD_COMPLETED_WORD:
      return { 
        ...state, 
        completedWords: new Set([...state.completedWords, action.payload])
      };
      
    case VOCABULARY_ACTIONS.RESET_COMPLETED_WORDS:
      return { ...state, completedWords: new Set() };
      
    case VOCABULARY_ACTIONS.SET_SHUFFLED_WORDS:
      return { ...state, shuffledWords: action.payload };
      
    case VOCABULARY_ACTIONS.SET_IS_RANDOM_MODE:
      return { ...state, isRandomMode: action.payload };
      
    // Legacy actions for backward compatibility
    case VOCABULARY_ACTIONS.SET_CURRENT_CATEGORY:
      return { 
        ...state, 
        currentCategory: action.payload,
        sessionProgress: {
          ...state.sessionProgress,
          currentWordIndex: 0,
          userAnswer: '',
          showResult: false
        }
      };
      
    case VOCABULARY_ACTIONS.SET_SHOW_CATEGORY_POPUP:
      return { ...state, showCategoryPopup: action.payload };
      
    case VOCABULARY_ACTIONS.SET_SHOW_SETTINGS_POPUP:
      return { ...state, showSettingsPopup: action.payload };
      
    case VOCABULARY_ACTIONS.RESET_QUIZ:
      return {
        ...state,
        score: { correct: 0, total: 0 },
        completedWords: new Set(),
        currentWordIndex: 0,
        userAnswer: '',
        showResult: false
      };
      
    default:
      return state;
  }
}

// Context
const VocabularyContext = createContext();

// Provider component
export function VocabularyProvider({ children }) {
  const [state, dispatch] = useReducer(vocabularyReducer, initialState);

  // Load vocabulary data
  useEffect(() => {
    const loadVocabulary = async () => {
      try {
        dispatch({ type: VOCABULARY_ACTIONS.SET_LOADING, payload: true });
        
        // Najpierw załaduj indeks kategorii
        const categoriesResponse = await fetch('/data/categories.json');
        if (!categoriesResponse.ok) {
          throw new Error('Nie udało się załadować indeksu kategorii');
        }
        const categoriesIndex = await categoriesResponse.json();
        
        // Następnie załaduj dane dla każdej puli
        const vocabularyData = {
          metadata: categoriesIndex.metadata,
          grammar_categories: categoriesIndex.grammar_categories,
          pools: {}
        };
        
        const loadPromises = Object.entries(categoriesIndex.pools).map(async ([poolCode, poolInfo]) => {
          try {
            const response = await fetch(`/data/${poolInfo.file}`);
            if (!response.ok) {
              throw new Error(`Nie udało się załadować puli: ${poolInfo.name}`);
            }
            const poolData = await response.json();
            
            // Zachowaj nową strukturę danych z pulami
            vocabularyData.pools[poolCode] = {
              ...poolInfo,
              words: poolData.words,
              metadata: poolData.metadata
            };
          } catch (err) {
            console.error(`Błąd ładowania puli ${poolCode}:`, err);
            throw err;
          }
        });
        
        await Promise.all(loadPromises);
        
        // Załaduj dane zdań
        console.log('Loading sentences data from /data/sentences.json...');
        const sentencesResponse = await fetch('/data/sentences.json');
        if (!sentencesResponse.ok) {
          throw new Error('Nie udało się załadować indeksu zdań');
        }
        const sentencesData = await sentencesResponse.json();
        
        console.log('Loaded sentences data:', sentencesData); // Debug log
        
        dispatch({ 
          type: VOCABULARY_ACTIONS.SET_VOCABULARY_DATA, 
          payload: vocabularyData 
        });
        
        dispatch({ 
          type: VOCABULARY_ACTIONS.SET_SENTENCES_DATA, 
          payload: sentencesData 
        });
      } catch (err) {
        dispatch({ 
          type: VOCABULARY_ACTIONS.SET_ERROR, 
          payload: err.message 
        });
      }
    };

    loadVocabulary();
  }, []);

  return (
    <VocabularyContext.Provider value={{ state, dispatch, actions: VOCABULARY_ACTIONS }}>
      {children}
    </VocabularyContext.Provider>
  );
}

// Custom hook to use the context
export function useVocabulary() {
  const context = useContext(VocabularyContext);
  if (!context) {
    throw new Error('useVocabulary must be used within a VocabularyProvider');
  }
  return context;
}

export { VOCABULARY_ACTIONS }; 